import { useEffect, useState } from "react";
import { GlowButton } from "@/components/ui/GlowButton";
import { Skeleton } from "@/components/ui/Skeleton";

type DailyFreeBandProps = {
  available: boolean;
  /** ISO timestamp of the next free pull — shown when `available` is false. */
  nextAt: string | null;
  loading: boolean;
  authenticated: boolean;
  /** Pull disabled while another pull is in flight. */
  disabled: boolean;
  onPull: () => void;
  onSignIn: () => void;
};

/** "in 5h 12m" under 24h, otherwise a compact local date-time. */
function formatNextFree(iso: string): string {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return "soon";
  const diffMs = target - Date.now();
  if (diffMs <= 0) return "soon";
  const totalMinutes = Math.ceil(diffMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours >= 24) {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
}

/** Draw shop — slim accent-tinted hairline band: free daily pack CTA or next-drop countdown. */
export function DailyFreeBand({
  available,
  nextAt,
  loading,
  authenticated,
  disabled,
  onPull,
  onSignIn,
}: DailyFreeBandProps) {
  // Re-render every minute while the countdown label is showing so
  // "Next in Xh Ym" stays current without a parent refetch.
  const [, setMinuteTick] = useState(0);
  useEffect(() => {
    if (available || !nextAt) return;
    const id = window.setInterval(() => setMinuteTick((tick) => tick + 1), 60_000);
    return () => window.clearInterval(id);
  }, [available, nextAt]);

  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-accent/30 bg-accent/8 px-4 py-2.5 sm:px-5">
      <p className="flex min-w-0 items-center gap-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-accent">
        {/* 아이콘 글리프 → 실제 팩 3D 렌더 썸네일 (엔트리 팩) */}
        <img
          src="/images/packs/ember.webp?v=5"
          alt=""
          aria-hidden
          draggable={false}
          className="h-8 w-auto shrink-0 drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]"
        />
        <span className="truncate">Free daily pack</span>
      </p>

      {loading ? (
        <Skeleton className="h-8 w-32 rounded-full" />
      ) : !authenticated ? (
        <GlowButton variant="ghost" onClick={onSignIn} className="shrink-0">
          Sign in to claim
        </GlowButton>
      ) : available ? (
        <GlowButton onClick={onPull} disabled={disabled} className="shrink-0">
          Open free pack
        </GlowButton>
      ) : (
        <p className="shrink-0 font-mono text-[10px] font-medium uppercase tracking-[0.14em] tabular-nums text-ink-mute">
          Next {nextAt ? formatNextFree(nextAt) : "tomorrow"}
        </p>
      )}
    </div>
  );
}
